import { useCallback, useEffect, useRef, useState } from 'react';
import { listNotifications, readNotification, readAllNotifications } from './notifications.service';

const EMPTY = { items: [], unreadCount: 0, nextBeforeId: null };
export const NOTIFICATION_POLL_MS = 15000;

// The header keys this hook by tenant and user, so inbox data never survives a session change.
export default function useTenantNotifications(enabled) {
  const [inbox, setInbox] = useState(EMPTY);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const alive = useRef(false);
  const requestRef = useRef(null);
  const mutationLock = useRef(false);
  const olderLoaded = useRef(false);

  const refresh = useCallback(async (beforeId = null, force = false) => {
    if (!enabled || !alive.current || (requestRef.current && !force)) return;
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    setLoading(true);
    try {
      const result = await listNotifications(beforeId, controller.signal);
      if (!alive.current || controller.signal.aborted) return;
      setInbox(previous => {
        const append = Boolean(beforeId);
        const preserveOlder = append || olderLoaded.current;
        const items = preserveOlder
          ? [...new Map([...previous.items, ...result.items].map(item => [item.id, item])).values()].sort((a, b) => b.id - a.id)
          : result.items;
        return { ...result, items, nextBeforeId: !append && olderLoaded.current ? previous.nextBeforeId : result.nextBeforeId };
      });
      if (beforeId) olderLoaded.current = true;
      setError('');
    } catch (failure) {
      if (alive.current && !controller.signal.aborted) setError(failure.message);
    } finally {
      if (requestRef.current === controller) {
        requestRef.current = null;
        if (alive.current) setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    alive.current = true;
    if (!enabled) { setInbox(EMPTY); setLoading(false); return () => { alive.current = false; }; }
    const onVisible = () => { if (document.visibilityState !== 'hidden' && !mutationLock.current) refresh(); };
    refresh();
    const timer = setInterval(onVisible, NOTIFICATION_POLL_MS);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive.current = false;
      requestRef.current?.abort();
      requestRef.current = null;
      clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, refresh]);

  const markRead = async (notification) => {
    if (mutationLock.current) return false;
    if (notification.readAt) return true;
    mutationLock.current = true;
    setBusy(true);
    try {
      await readNotification(notification.id);
      if (!alive.current) return false;
      setInbox(previous => ({ ...previous,
        items: previous.items.map(item => item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item),
      }));
      await refresh(null, true);
      return true;
    } catch (failure) { if (alive.current) setError(failure.message); return false; }
    finally { mutationLock.current = false; if (alive.current) setBusy(false); }
  };

  const markAllRead = async () => {
    const throughId = inbox.items[0]?.id;
    if (!throughId || mutationLock.current) return;
    mutationLock.current = true;
    setBusy(true);
    try {
      await readAllNotifications(throughId);
      if (!alive.current) return;
      setInbox(previous => ({ ...previous, items: previous.items.map(item => item.id <= throughId ? { ...item, readAt: item.readAt || new Date().toISOString() } : item) }));
      await refresh(null, true);
    } catch (failure) { if (alive.current) setError(failure.message); }
    finally { mutationLock.current = false; if (alive.current) setBusy(false); }
  };

  return { ...inbox, loading, error, busy, refresh, markRead, markAllRead, loadMore: () => refresh(inbox.nextBeforeId) };
}
